import { isValid } from 'date-fns';
import { setHours, setMinutes, startOfDay } from 'date-fns';

/**
 * CALENDAR UTILITIES
 * Hard-locked to "Wall Clock" numerical values.
 * STRICT FAIL MODEL: Throws errors on invalid data to prevent UI ghosting.
 */

/**
 * Calculates the vertical 'top' position in pixels.
 * Expects a Date object already zoned by the CalendarEngine.
 */
export const getEventPosition = (date: Date, hourHeight: number, allDayRowHeight: number): number => {
  if (!date) {
    throw new Error("getEventPosition: Date is null or undefined.");
  }
  
  if (!isValid(date)) {
    throw new Error(`getEventPosition: Received an invalid Date object. Data: ${date}`);
  }

  if (!hourHeight || hourHeight <= 0) {
    throw new Error(`getEventPosition: Invalid hourHeight provided: ${hourHeight}`);
  }

  /**
   * THE "TIMEZONE BLIND" EXTRACTION:
   * We use local getters because the object has already been shifted 
   * to the user's context. We want the literal hour digits.
   */
  const hours = date.getHours();
  const minutes = date.getMinutes();
  
  const totalMinutesFromMidnight = (hours * 60) + minutes;
  
  // Calculate position relative to the timed grid, then add the all-day row offset
  return allDayRowHeight + (totalMinutesFromMidnight / 60) * hourHeight;
};

/**
 * Calculates the vertical 'height' in pixels based on duration.
 * Uses numerical extraction to ensure height isn't affected by hidden browser offsets.
 */
export const getEventHeight = (start: Date, end: Date, hourHeight: number): number => { // Corrected: hourHeight
  if (!start || !end) {
    throw new Error("getEventHeight: Start or End date is missing.");
  }

  if (!isValid(start) || !isValid(end)) {
    throw new Error(`getEventHeight: Invalid Date objects provided. Start: ${start}, End: ${end}`);
  }

  // Calculate start minutes (Wall Clock)
  const startTotal = (start.getHours() * 60) + start.getMinutes();
  
  // Calculate end minutes (Wall Clock)
  let endTotal = (end.getHours() * 60) + end.getMinutes();

  /**
   * Midnight Overflow Fix:
   * If the event ends at midnight of the next day (00:00), we treat it as 1440 
   * (the final minute of the current 24h grid) to prevent zero or negative heights.
   */
  if (endTotal === 0 && end.getDate() !== start.getDate()) {
    endTotal = 1440;
  }

  const durationInMinutes = endTotal - startTotal;

  if (durationInMinutes < 0) {
    throw new Error(`getEventHeight: Calculated negative duration (${durationInMinutes} mins). Check zoned date order.`);
  }
  
  const height = (durationInMinutes / 60) * hourHeight; // Corrected: hourHeight
  
  // Hard minimum for UI visibility
  return Math.max(height, 24);
};

/**
 * Utility to snap pixel coordinates to the nearest interval (e.g., 15 mins).
 */
export const snapToInterval = (y: number, hourHeight: number, intervalMinutes: number = 15): number => { // Corrected: hourHeight
  if (y < 0) return 0;

  const minutesPerPixel = 60 / hourHeight; // How many minutes does 1 pixel represent
  const totalMinutes = y * minutesPerPixel; // Total minutes represented by y pixels
  
  const snappedMinutes = Math.round(totalMinutes / intervalMinutes) * intervalMinutes;
  
  return (snappedMinutes / 60) * hourHeight; // Corrected: hourHeight
};

/**
 * 🌟 NEW: Converts a pixel offset from the top of the timed grid to minutes from midnight.
 */
export const pixelsToMinutes = (pixels: number, hourHeight: number): number => {
  if (hourHeight <= 0) return 0;
  // Calculate minutes from the timed grid (excluding all-day row)
  return (pixels / hourHeight) * 60;
};

/**
 * 🌟 NEW: Converts a pixel offset to a Date object, snapped to a specified interval.
 * `yOffsetPixels` should be the pixel distance *from the top of the timed grid section*,
 * meaning it should *exclude* the allDayRowHeight.
 */
export const pixelsToDateTime = (
  yOffsetPixels: number,
  targetDate: Date, // The date for the specific column
  hourHeight: number,
  intervalMinutes: number = 15
): Date => {
  const totalMinutes = pixelsToMinutes(yOffsetPixels, hourHeight);
  const snappedMinutes = Math.round(totalMinutes / intervalMinutes) * intervalMinutes;

  const startOfTargetDay = startOfDay(targetDate);
  const hours = Math.floor(snappedMinutes / 60);
  const minutes = snappedMinutes % 60;

  let result = setHours(startOfTargetDay, hours);
  result = setMinutes(result, minutes);

  return result;
};